import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container, Paper, Typography, Box, Button, IconButton, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, Slider, Select, MenuItem, FormControl, InputLabel, Tooltip,
  Chip, Alert, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, TextField, LinearProgress, Card,
  CardContent, Divider, Stack, Snackbar, ButtonGroup, Menu, FormControlLabel,
  Link
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  ArrowBack,
  AutoFixHigh as AutoFixHighIcon,
  Psychology as PsychologyIcon,
  Assessment as AssessmentIcon,
  East as EastIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  DoneAll as DoneAllIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  Send as SendIcon,
  InfoOutlined as InfoOutlinedIcon,
  LocalOffer as LocalOfferIcon,
  AccountBalance as AccountBalanceIcon,
  Check as CheckIcon,
  Block as BlockIcon,
  Restore as RestoreIcon,
  ArrowDropDown as ArrowDropDownIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';

import AppContext from '../components/AppContext';
import useBack from '../hooks/useBack';
import { listAnys } from '../functions/common-lists';
import { formatEuro } from '../functions/formatNumber';
import { getColorForAmount } from '../functions/colors';
import { strDateToDDMMYYYY } from '../functions/formatDate';
import { ConcepteIcon } from '../components/common/IconMap';
import SelectConcepteTree from '../components/common/SelectConcepteTree';
import {
  getRefineCandidates,
  executeRefineItem,
  executeRefine,
  classifyText,
  getMovimentsAlignment
} from '../services/concepteGraphService';

const RefinamentJerarquicPage = () => {
  const { userapp: paramUserapp, anyo: paramAnyo } = useParams();
  const { onBack } = useBack();
  const { ctxUser, ctxApp, conceptesTree, entitatsTransversals } = useContext(AppContext);
  const jwtToken = ctxUser?.fdvToken?.jwtToken || '';
  const userapp = paramUserapp || ctxApp?.userapp || '';

  // Pestanya activa (0: Candidats, 1: Provador, 2: Auditoria)
  const [activeTab, setActiveTab] = useState(0);

  // Sub-pestanya d'estat a Candidats: 'PENDENTS' o 'DESCARTATS'
  const [refineEstat, setRefineEstat] = useState('PENDENTS');
  const [counts, setCounts] = useState({ pendents: 0, descartats: 0 });

  // Filtres per als candidats
  const currentYear = new Date().getFullYear();
  const [selectedAny, setSelectedAny] = useState(paramAnyo ? parseInt(paramAnyo, 10) : currentYear);
  const [minConfidence, setMinConfidence] = useState(0.90);

  // Estat dels candidats
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [lastSearchedParams, setLastSearchedParams] = useState(null);

  // Menú d'accions per acceptar a tots els iguals (Split Button per fila)
  const [splitMenuAnchor, setSplitMenuAnchor] = useState(null);
  const [splitMenuCandidate, setSplitMenuCandidate] = useState(null);

  // Modals de confirmació en bloc
  const [confirmSelectedOpen, setConfirmSelectedOpen] = useState(false);
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  // Notificacions Toast
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  // Canvi manual de node proposat per a una fila
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [selectedTreeOption, setSelectedTreeOption] = useState(null);
  const [manualAplicarIguals, setManualAplicarIguals] = useState(false);
  const [treeDialogOpen, setTreeDialogOpen] = useState(false);

  // Modal de Context Bancari Complet
  const [contextCandidate, setContextCandidate] = useState(null);
  const [contextDialogOpen, setContextDialogOpen] = useState(false);

  const handleOpenContextDialog = (candidate) => {
    setContextCandidate(candidate);
    setContextDialogOpen(true);
  };

  // Estat del Provador Semàntic
  const [testerText, setTesterText] = useState('');
  const [testerLoading, setTesterLoading] = useState(false);
  const [testerResult, setTesterResult] = useState(null);

  // Estat de l'Auditoria d'Alineació
  const [alignmentData, setAlignmentData] = useState(null);
  const [alignmentLoading, setAlignmentLoading] = useState(false);

  const anysDisponibles = useMemo(() => listAnys(10), []);

  // Carregar candidats de refinament i comptadors
  const fetchCandidates = useCallback(async (anyToFetch, confToFetch, estatToFetch = refineEstat) => {
    if (!jwtToken || !userapp) return;
    setLoadingCandidates(true);
    try {
      // Consultem en paral·lel pendents i descartats per mantenir els comptadors actualitzats
      const [pendentsData, descartatsData] = await Promise.all([
        getRefineCandidates(jwtToken, userapp, anyToFetch, confToFetch, 'PENDENTS'),
        getRefineCandidates(jwtToken, userapp, anyToFetch, confToFetch, 'DESCARTATS')
      ]);

      const pendentsList = pendentsData || [];
      const descartatsList = descartatsData || [];
      setCounts({
        pendents: pendentsList.length,
        descartats: descartatsList.length
      });

      const activeList = estatToFetch === 'DESCARTATS' ? descartatsList : pendentsList;
      const formatted = activeList.map(item => ({
        ...item,
        isCustomized: false
      }));

      setCandidates(formatted);
      setSelectedIds(new Set(formatted.map(c => c.concepteMovimentId)));
      setLastSearchedParams({ any: anyToFetch, minConfidence: confToFetch, estat: estatToFetch });
    } catch (err) {
      console.error("Error obtenint candidats a refinament:", err);
      setNotification({
        open: true,
        message: "Error al carregar els candidats de refinament: " + (err.response?.data?.message || err.message),
        severity: 'error'
      });
      setCandidates([]);
      setSelectedIds(new Set());
    } finally {
      setLoadingCandidates(false);
    }
  }, [jwtToken, userapp, refineEstat]);

  // Carregar automàticament en entrar
  useEffect(() => {
    if (jwtToken && userapp) {
      fetchCandidates(selectedAny, minConfidence, refineEstat);
    }
  }, [jwtToken, userapp]); // eslint-disable-line react-hooks/exhaustive-deps

  // Canvi de sub-pestanya d'estat
  const handleEstatChange = (newEstat) => {
    setRefineEstat(newEstat);
    fetchCandidates(selectedAny, minConfidence, newEstat);
  };

  // Gestió de Selecció de Candidats
  const handleToggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(new Set(candidates.map(c => c.concepteMovimentId)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleToggleRow = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const isAllSelected = candidates.length > 0 && selectedIds.size === candidates.length;
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < candidates.length;

  // Acceptar proposta d'un moviment (individual o per a tots els iguals)
  const handleAcceptItem = async (candidate, aplicarATotsElsIguals = false) => {
    if (!candidate) return;
    try {
      const itemData = {
        concepteMovimentId: candidate.concepteMovimentId,
        accio: 'ACCEPTAR',
        conceptePath: candidate.pathProposat || candidate.nodeProposatNom,
        nodeFullaId: candidate.nodeFullaId || candidate.nodeProposatId,
        aplicarATotsElsIguals
      };
      const res = await executeRefineItem(jwtToken, userapp, itemData);
      const afectats = res?.afectatsCount || 1;
      setNotification({
        open: true,
        message: `Proposta acceptada amb èxit! (${afectats} moviment${afectats > 1 ? 's actualitzats' : ' actualitzat'})`,
        severity: 'success'
      });

      if (aplicarATotsElsIguals && afectats > 1) {
        fetchCandidates(selectedAny, minConfidence, refineEstat);
      } else {
        setCandidates(prev => prev.filter(c => c.concepteMovimentId !== candidate.concepteMovimentId));
        setSelectedIds(prev => {
          const next = new Set(prev);
          next.delete(candidate.concepteMovimentId);
          return next;
        });
        setCounts(prev => ({
          ...prev,
          [refineEstat === 'PENDENTS' ? 'pendents' : 'descartats']: Math.max(0, prev[refineEstat === 'PENDENTS' ? 'pendents' : 'descartats'] - 1)
        }));
      }
    } catch (err) {
      console.error("Error acceptant proposta:", err);
      setNotification({
        open: true,
        message: "Error acceptant proposta: " + (err.response?.data?.message || err.message),
        severity: 'error'
      });
    }
  };

  // Descartar una proposta individual (guarda a RefinamentDescartat)
  const handleDiscardItem = async (candidate) => {
    if (!candidate) return;
    try {
      const itemData = {
        concepteMovimentId: candidate.concepteMovimentId,
        accio: 'DESCARTAR'
      };
      await executeRefineItem(jwtToken, userapp, itemData);
      setNotification({
        open: true,
        message: "Moviment marcat com a descartat (es pot revisar a la sub-pestanya 'Descartats').",
        severity: 'info'
      });
      setCandidates(prev => prev.filter(c => c.concepteMovimentId !== candidate.concepteMovimentId));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(candidate.concepteMovimentId);
        return next;
      });
      setCounts(prev => ({
        pendents: Math.max(0, prev.pendents - 1),
        descartats: prev.descartats + 1
      }));
    } catch (err) {
      console.error("Error descartant moviment:", err);
      setNotification({
        open: true,
        message: "Error descartant moviment: " + (err.response?.data?.message || err.message),
        severity: 'error'
      });
    }
  };

  // Restaurar un moviment prèviament descartat cap a pendents
  const handleRestoreItem = async (candidate) => {
    if (!candidate) return;
    try {
      const itemData = {
        concepteMovimentId: candidate.concepteMovimentId,
        accio: 'RESTAURAR'
      };
      await executeRefineItem(jwtToken, userapp, itemData);
      setNotification({
        open: true,
        message: "Moviment restaurat correctament a pendents!",
        severity: 'success'
      });
      setCandidates(prev => prev.filter(c => c.concepteMovimentId !== candidate.concepteMovimentId));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(candidate.concepteMovimentId);
        return next;
      });
      setCounts(prev => ({
        descartats: Math.max(0, prev.descartats - 1),
        pendents: prev.pendents + 1
      }));
    } catch (err) {
      console.error("Error restaurant moviment:", err);
      setNotification({
        open: true,
        message: "Error restaurant moviment: " + (err.response?.data?.message || err.message),
        severity: 'error'
      });
    }
  };

  // Obrir diàleg de selecció de concepte canònic
  const handleOpenTreeSelector = (candidate) => {
    setEditingCandidate(candidate);
    setSelectedTreeOption(candidate.nodeProposatId ? {
      id: candidate.nodeProposatId,
      nom: candidate.nodeProposatNom,
      codi: candidate.nodeProposatCodi,
      conceptePath: candidate.pathProposat || candidate.nodeProposatNom
    } : null);
    setManualAplicarIguals(false);
    setTreeDialogOpen(true);
  };

  // Aplicar canvi manual de concepte
  const handleApplyManualSelection = async () => {
    if (!editingCandidate || !selectedTreeOption) return;
    try {
      const itemData = {
        concepteMovimentId: editingCandidate.concepteMovimentId,
        accio: 'MANUAL',
        conceptePath: selectedTreeOption.conceptePath || selectedTreeOption.pathLabel || selectedTreeOption.nom,
        nodeFullaId: selectedTreeOption.id,
        aplicarATotsElsIguals: manualAplicarIguals
      };
      const res = await executeRefineItem(jwtToken, userapp, itemData);
      const afectats = res?.afectatsCount || 1;
      setNotification({
        open: true,
        message: `Concepte assignat manualment amb èxit! (${afectats} moviment${afectats > 1 ? 's actualitzats' : ' actualitzat'})`,
        severity: 'success'
      });
      setTreeDialogOpen(false);
      setEditingCandidate(null);
      setSelectedTreeOption(null);
      setManualAplicarIguals(false);

      if (manualAplicarIguals && afectats > 1) {
        fetchCandidates(selectedAny, minConfidence, refineEstat);
      } else {
        setCandidates(prev => prev.filter(c => c.concepteMovimentId !== editingCandidate.concepteMovimentId));
        setSelectedIds(prev => {
          const next = new Set(prev);
          next.delete(editingCandidate.concepteMovimentId);
          return next;
        });
        setCounts(prev => ({
          ...prev,
          [refineEstat === 'PENDENTS' ? 'pendents' : 'descartats']: Math.max(0, prev[refineEstat === 'PENDENTS' ? 'pendents' : 'descartats'] - 1)
        }));
      }
    } catch (err) {
      console.error("Error aplicant canvi manual:", err);
      setNotification({
        open: true,
        message: "Error aplicant canvi manual: " + (err.response?.data?.message || err.message),
        severity: 'error'
      });
    }
  };

  // Renderització del Tooltip ric complet per a la cel·la de Moviment a Desktop
  const renderMovementTooltip = (cand) => {
    const desc = cand.descripcio || cand.concepteDescripcio || cand.movimentDescripcio || cand.observacions;
    return (
      <Box sx={{ p: 0.5, maxWidth: 360 }}>
        {desc && (
          <Box sx={{ mb: 1, p: 0.75, borderRadius: 1, backgroundColor: 'rgba(2, 136, 209, 0.15)', border: '1px solid #0288d1' }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#0288d1', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocalOfferIcon sx={{ fontSize: 13 }} /> DESCRIPCIÓ ARTICLE:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#01579b', mt: 0.25 }}>
              {desc}
            </Typography>
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          {cand.movimentNom || 'N/D'}
        </Typography>

        <Stack spacing={0.3} sx={{ fontSize: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.15)', pt: 0.5 }}>
          {cand.entitatOrigen && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ opacity: 0.8 }}>Entitat:</span> <strong>{cand.entitatOrigen}</strong>
            </Box>
          )}
          {cand.contracte && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ opacity: 0.8 }}>Contracte:</span> <strong>{cand.contracte}</strong>
            </Box>
          )}
          {cand.dataValor && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ opacity: 0.8 }}>Data valor:</span> <strong>{strDateToDDMMYYYY(cand.dataValor)}</strong>
            </Box>
          )}
          {cand.saldo != null && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ opacity: 0.8 }}>Saldo:</span> <strong>{formatEuro(cand.saldo)}</strong>
            </Box>
          )}
          {cand.concepteClassic && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ opacity: 0.8 }}>Concepte clàssic:</span> <strong>{cand.concepteClassic}</strong>
            </Box>
          )}
          {cand.mesInfo && (
            <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed rgba(255,255,255,0.2)' }}>
              <span style={{ opacity: 0.8 }}>Més info:</span> <span style={{ fontStyle: 'italic' }}>{cand.mesInfo}</span>
            </Box>
          )}
        </Stack>
      </Box>
    );
  };

  // Execució del refinament seleccionat
  const handleExecuteSelected = async () => {
    if (selectedIds.size === 0) return;
    setIsExecuting(true);
    try {
      // Preparem el payload amb només els ítems seleccionats
      const itemsToExecute = candidates
        .filter(c => selectedIds.has(c.concepteMovimentId))
        .map(c => ({
          concepteMovimentId: c.concepteMovimentId,
          acceptarProposta: true,
          nodeN1Id: c.nodeN1Id,
          nodeN2Id: c.nodeN2Id,
          nodeN3Id: c.nodeN3Id,
          nodeFullaId: c.nodeFullaId || c.nodeProposatId
        }));

      const res = await executeRefine(jwtToken, userapp, selectedAny, itemsToExecute);
      setNotification({
        open: true,
        message: `S'han refinat amb èxit ${res?.updatedCount || itemsToExecute.length} moviments!`,
        severity: 'success'
      });
      setConfirmSelectedOpen(false);
      // Recarreguem la llista per veure els canvis
      fetchCandidates(selectedAny, minConfidence);
    } catch (err) {
      console.error("Error aplicant refinament seleccionat:", err);
      setNotification({
        open: true,
        message: "Error aplicant el refinament: " + (err.response?.data?.message || err.message),
        severity: 'error'
      });
    } finally {
      setIsExecuting(false);
    }
  };

  // Execució massiva de tots els candidats
  const handleExecuteAll = async () => {
    setIsExecuting(true);
    try {
      // Cos buit per indicar al backend que accepti totes les propostes automàtiques
      const res = await executeRefine(jwtToken, userapp, selectedAny, []);
      setNotification({
        open: true,
        message: `S'ha executat l'aprovació automàtica! Registres actualitzats: ${res?.updatedCount || 0}`,
        severity: 'success'
      });
      setConfirmAllOpen(false);
      fetchCandidates(selectedAny, minConfidence);
    } catch (err) {
      console.error("Error aplicant aprovació automàtica:", err);
      setNotification({
        open: true,
        message: "Error en l'aprovació automàtica: " + (err.response?.data?.message || err.message),
        severity: 'error'
      });
    } finally {
      setIsExecuting(false);
    }
  };

  // Execució del Provador Semàntic
  const handleRunTester = async (textToTest) => {
    const text = textToTest || testerText;
    if (!text || !text.trim()) return;
    setTesterLoading(true);
    try {
      const res = await classifyText(jwtToken, userapp, text.trim());
      setTesterResult(res);
    } catch (err) {
      console.error("Error al provador semàntic:", err);
      setNotification({
        open: true,
        message: "Error analitzant el text: " + (err.response?.data?.message || err.message),
        severity: 'error'
      });
    } finally {
      setTesterLoading(false);
    }
  };

  // Carregar auditoria d'alineació
  const handleFetchAlignment = async () => {
    if (!jwtToken || !userapp) return;
    setAlignmentLoading(true);
    try {
      const data = await getMovimentsAlignment(jwtToken, userapp);
      setAlignmentData(data || []);
    } catch (err) {
      console.error("Error obtenint auditoria d'alineació:", err);
      setNotification({
        open: true,
        message: "Error consultant l'auditoria d'alineació: " + (err.response?.data?.message || err.message),
        severity: 'error'
      });
    } finally {
      setAlignmentLoading(false);
    }
  };

  // Colors segons nivell de confiança
  const getConfidenceColor = (conf) => {
    const pct = Math.round(conf * 100);
    if (pct >= 95) return 'success';
    if (pct >= 85) return 'primary';
    if (pct >= 75) return 'warning';
    return 'error';
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      {/* Capçalera adaptada a mòbil i escriptori */}
      <Box sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 1.5,
        mb: 2.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={onBack} title="Tornar">
            <ArrowBack />
          </IconButton>
          <AutoFixHighIcon color="primary" sx={{ fontSize: { xs: 26, sm: 32 } }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              Refinament Jeràrquic de Conceptes
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Afinament intel·ligent de moviments bancaris des de conceptes N1/N2 cap a branques específiques N2/N3
            </Typography>
          </Box>
        </Box>

        <Chip
          label={`Compte actiu: ${userapp}`}
          color="info"
          variant="outlined"
          sx={{ fontWeight: 'bold', alignSelf: { xs: 'flex-start', sm: 'auto' } }}
        />
      </Box>

      {/* Pestanyes de navegació scrollables en mòbil */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<PlaylistAddCheckIcon />}
            iconPosition="start"
            label={`Candidats a Refinar ${candidates.length > 0 ? `(${candidates.length})` : ''}`}
            sx={{ fontWeight: 600, minHeight: 48 }}
          />
          <Tab
            icon={<PsychologyIcon />}
            iconPosition="start"
            label="Provador Semàntic"
            sx={{ fontWeight: 600, minHeight: 48 }}
          />
          <Tab
            icon={<AssessmentIcon />}
            iconPosition="start"
            label="Auditoria d'Alineació"
            sx={{ fontWeight: 600, minHeight: 48 }}
          />
        </Tabs>
      </Paper>

      {/* ========================================================================= */}
      {/* PESTANYA 0: CANDIDATS A REFINAR                                          */}
      {/* ========================================================================= */}
      {activeTab === 0 && (
        <Stack spacing={2.5}>
          {/* Panell de Filtres */}
          <Paper sx={{ p: 2.5, borderRadius: 2, background: 'linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)' }}>
            <Grid container spacing={3} alignItems="center">
              {/* Selector d'Any */}
              <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="refine-any-label">Exercici / Any</InputLabel>
                  <Select
                    labelId="refine-any-label"
                    value={selectedAny || 'ALL'}
                    label="Exercici / Any"
                    onChange={(e) => setSelectedAny(e.target.value === 'ALL' ? 'ALL' : Number(e.target.value))}
                  >
                    <MenuItem value="ALL">Tots els anys (Sense filtre)</MenuItem>
                    {anysDisponibles.map((a) => (
                      <MenuItem key={a} value={a}>{a}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Slider de Confiança Mínima */}
              <Grid size={{ xs: 12, sm: 5, md: 6 }}>
                <Box sx={{ px: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Confiança mínima requerida:
                    </Typography>
                    <Chip
                      label={`${Math.round(minConfidence * 100)}%`}
                      size="small"
                      color={getConfidenceColor(minConfidence)}
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                  <Slider
                    value={minConfidence}
                    min={0.50}
                    max={1.00}
                    step={0.05}
                    marks={[
                      { value: 0.50, label: '50%' },
                      { value: 0.75, label: '75%' },
                      { value: 0.90, label: '90%' },
                      { value: 1.00, label: '100%' }
                    ]}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
                    onChange={(e, val) => setMinConfidence(val)}
                  />
                </Box>
              </Grid>

              {/* Botó Buscar Candidats */}
              <Grid size={{ xs: 12, sm: 3, md: 3 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={loadingCandidates ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  disabled={loadingCandidates}
                  onClick={() => fetchCandidates(selectedAny, minConfidence, refineEstat)}
                  sx={{ py: 1, fontWeight: 'bold', borderRadius: 1.5 }}
                >
                  {loadingCandidates ? 'Cercant...' : 'Buscar Candidats'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Sub-pestanyes d'Estat: Pendents de Refinar vs Descartats */}
          <Paper sx={{ borderRadius: 2 }}>
            <Tabs
              value={refineEstat}
              onChange={(e, val) => handleEstatChange(val)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                value="PENDENTS"
                label={`Pendents de Refinar (${counts.pendents})`}
                sx={{ fontWeight: 700, textTransform: 'none', minHeight: 48 }}
              />
              <Tab
                value="DESCARTATS"
                label={`Descartats (${counts.descartats})`}
                sx={{
                  fontWeight: 700,
                  textTransform: 'none',
                  minHeight: 48,
                  color: counts.descartats > 0 ? 'text.secondary' : undefined
                }}
              />
            </Tabs>
          </Paper>

          {/* Banner informatiu per a la sub-pestanya de Descartats */}
          {refineEstat === 'DESCARTATS' && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Aquests moviments han estat descartats per no aplicar la proposta automàtica del motor.
              Pots restaurar-los a pendents en qualsevol moment o reassignar-los un concepte canònic manualment.
            </Alert>
          )}

          {/* Barra d'accions i selecció en bloc adaptada a mòbil */}
          <Paper sx={{
            p: 2,
            borderRadius: 2,
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Checkbox
                checked={isAllSelected}
                indeterminate={isSomeSelected}
                onChange={handleToggleSelectAll}
                disabled={candidates.length === 0}
              />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selectedIds.size > 0
                  ? `${selectedIds.size} seleccionats de ${candidates.length} ${refineEstat === 'PENDENTS' ? 'candidats' : 'descartats'}`
                  : `0 seleccionats (${candidates.length} disponibles)`}
              </Typography>
            </Box>

            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5,
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                disabled={selectedIds.size === 0 || isExecuting}
                onClick={() => setConfirmSelectedOpen(true)}
                sx={{ fontWeight: 'bold', width: { xs: '100%', sm: 'auto' } }}
              >
                Aplicar Refinament Seleccionat ({selectedIds.size})
              </Button>

              {refineEstat === 'PENDENTS' && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<DoneAllIcon />}
                  disabled={candidates.length === 0 || isExecuting}
                  onClick={() => setConfirmAllOpen(true)}
                  sx={{ fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}
                >
                  Aprovar tots automàticament
                </Button>
              )}
            </Box>
          </Paper>

          {/* ========================================================================= */}
          {/* VISTA DUAL: TAULA A DESKTOP (8 COLUMNES, ZERO SCROLL) I TARGETES A MÒBIL  */}
          {/* ========================================================================= */}

          {/* 1. Vista Taula a Desktop (md en endavant) - 8 Columnes sense scroll horitzontal */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <TableContainer sx={{ maxHeight: 640 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ backgroundColor: '#f4f6f8' }}>
                        <Checkbox
                          checked={isAllSelected}
                          indeterminate={isSomeSelected}
                          onChange={handleToggleSelectAll}
                          disabled={candidates.length === 0}
                        />
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#f4f6f8', fontWeight: 'bold', width: '90px' }}>Data</TableCell>
                      <TableCell sx={{ backgroundColor: '#f4f6f8', fontWeight: 'bold', minWidth: '200px' }}>Moviment Bancari</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#f4f6f8', fontWeight: 'bold', width: '105px' }}>Import</TableCell>
                      <TableCell sx={{ backgroundColor: '#f4f6f8', fontWeight: 'bold', width: '280px' }}>Transició de Concepte (Actual → Proposta)</TableCell>
                      <TableCell align="center" sx={{ backgroundColor: '#f4f6f8', fontWeight: 'bold', width: '95px' }}>Confiança</TableCell>
                      <TableCell sx={{ backgroundColor: '#f4f6f8', fontWeight: 'bold', minWidth: '190px' }}>Criteri & Tokens</TableCell>
                      <TableCell align="center" sx={{ backgroundColor: '#f4f6f8', fontWeight: 'bold', width: '180px' }}>Accions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loadingCandidates ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                          <CircularProgress size={36} />
                          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                            {refineEstat === 'PENDENTS'
                              ? "Analitzant l'històric i cercant patrons d'afinament..."
                              : "Carregant moviments descartats..."}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : candidates.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                          <Box sx={{ color: 'text.secondary', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {refineEstat === 'PENDENTS' ? 'Tot afinat! Cap candidat pendent.' : 'No hi ha moviments descartats.'}
                            </Typography>
                            <Typography variant="body2">
                              {refineEstat === 'PENDENTS'
                                ? (lastSearchedParams
                                  ? `No hi ha moviments pendents de refinar per a l'any ${lastSearchedParams.any} amb confiança >= ${Math.round(lastSearchedParams.minConfidence * 100)}%.`
                                  : "No s'han trobat moviments per refinar amb els filtres indicats.")
                                : "Quan descartis propostes de refinament, apareixeran aquí per si vols reconsiderar-les."}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      candidates.map((cand) => {
                        const isSelected = selectedIds.has(cand.concepteMovimentId);
                        const colorImport = getColorForAmount(cand.importe, false);
                        const confColor = getConfidenceColor(cand.confianca);
                        const confPct = Math.round((cand.confianca || 0) * 100);
                        const desc = cand.descripcio || cand.concepteDescripcio || cand.movimentDescripcio || cand.observacions;

                        return (
                          <TableRow
                            key={cand.concepteMovimentId}
                            hover
                            selected={isSelected}
                            sx={{
                              backgroundColor: cand.isCustomized ? 'rgba(232, 245, 233, 0.4)' : undefined,
                              '&:hover': { backgroundColor: isSelected ? '#e3f2fd' : undefined }
                            }}
                          >
                            {/* Checkbox de selecció */}
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleToggleRow(cand.concepteMovimentId)}
                              />
                            </TableCell>

                            {/* Data */}
                            <TableCell sx={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                              {strDateToDDMMYYYY(cand.data)}
                            </TableCell>

                            {/* Moviment Bancari amb Tooltip ric complet */}
                            <TableCell sx={{ maxWidth: '240px' }}>
                              <Tooltip
                                title={renderMovementTooltip(cand)}
                                arrow
                                placement="top-start"
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      backgroundColor: '#0f172a',
                                      color: '#f8fafc',
                                      boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                                      fontSize: '0.8rem',
                                      maxWidth: 380,
                                      p: 1.2,
                                      border: '1px solid rgba(255,255,255,0.1)'
                                    }
                                  }
                                }}
                              >
                                <Box sx={{ cursor: 'pointer' }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {cand.movimentNom || 'N/D'}
                                  </Typography>

                                  {/* Micro-indicador de descripció si en té */}
                                  {desc && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.4,
                                        color: '#0288d1',
                                        fontWeight: 600,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '220px',
                                        mt: 0.2
                                      }}
                                    >
                                      <LocalOfferIcon sx={{ fontSize: 11 }} />
                                      {desc}
                                    </Typography>
                                  )}

                                  {/* ID i botó ràpid per obrir el modal de context */}
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                                    {cand.concepteMovimentId && (
                                      <Link
                                        href={`/${userapp}/cmovdetail/${cand.concepteMovimentId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        title="Obrir fitxa del concepte-moviment en una nova pestanya"
                                        sx={{
                                          fontSize: '0.72rem',
                                          fontWeight: 600,
                                          color: 'text.secondary',
                                          textDecoration: 'none',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 0.3,
                                          '&:hover': {
                                            color: 'primary.main',
                                            textDecoration: 'underline'
                                          }
                                        }}
                                      >
                                        #{cand.concepteMovimentId}
                                        <OpenInNewIcon sx={{ fontSize: '10px !important', opacity: 0.7 }} />
                                      </Link>
                                    )}
                                    <Button
                                      size="small"
                                      variant="text"
                                      startIcon={<InfoOutlinedIcon sx={{ fontSize: '13px !important' }} />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenContextDialog(cand);
                                      }}
                                      sx={{
                                        p: 0,
                                        minWidth: 0,
                                        fontSize: '0.68rem',
                                        textTransform: 'none',
                                        color: 'primary.main',
                                        fontWeight: 500,
                                        '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' }
                                      }}
                                    >
                                      Context
                                    </Button>
                                  </Box>
                                </Box>
                              </Tooltip>
                            </TableCell>

                            {/* Import */}
                            <TableCell align="right" sx={{ color: colorImport, fontWeight: 'bold', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                              {formatEuro(cand.importe)}
                            </TableCell>

                            {/* Transició de Concepte: Actual -> Proposat */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'nowrap' }}>
                                {/* Node Actual */}
                                <Tooltip title={`Path actual: ${cand.pathActual || cand.nodeActualNom || ''}`}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: '120px' }}>
                                    <ConcepteIcon code={cand.nodeActualCodi} concept={cand.nodeActualNom} level={cand.nodeActualNivell || 1} onlyIcon />
                                    <Chip
                                      label={cand.nodeActualNom || cand.pathActual || 'GENÈRIC'}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        fontSize: '0.72rem',
                                        maxWidth: '100px',
                                        borderColor: '#b0bec5'
                                      }}
                                    />
                                  </Box>
                                </Tooltip>

                                <EastIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />

                                {/* Node Proposat */}
                                <Tooltip title={`Proposta refinada: ${cand.pathProposat || cand.nodeProposatNom}`}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: '135px' }}>
                                    <ConcepteIcon code={cand.nodeProposatCodi} concept={cand.nodeProposatNom} level={cand.nodeProposatNivell || 2} onlyIcon />
                                    <Chip
                                      label={cand.nodeProposatNom}
                                      size="small"
                                      color={cand.isCustomized ? "success" : "primary"}
                                      sx={{
                                        fontSize: '0.72rem',
                                        fontWeight: 'bold',
                                        maxWidth: '115px'
                                      }}
                                    />
                                  </Box>
                                </Tooltip>
                              </Box>

                              {/* Detall del Path proposat */}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.25, fontSize: '0.65rem' }}
                              >
                                {cand.pathProposat || cand.nodeProposatNom}
                              </Typography>
                            </TableCell>

                            {/* Confiança */}
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                <Chip
                                  label={`${confPct}%`}
                                  size="small"
                                  color={confColor}
                                  sx={{ fontWeight: 'bold', height: '22px', fontSize: '0.72rem' }}
                                />
                                <LinearProgress
                                  variant="determinate"
                                  value={confPct}
                                  color={confColor}
                                  sx={{ width: '50px', height: 4, borderRadius: 2 }}
                                />
                              </Box>
                            </TableCell>

                            {/* Criteri / Motiu i Tokens Avaluats */}
                            <TableCell sx={{ minWidth: '190px', maxWidth: '260px' }}>
                              <Tooltip title={cand.criteriDecisio || cand.motiu || ''}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    color: 'text.secondary',
                                    fontSize: '0.72rem',
                                    lineHeight: 1.25
                                  }}
                                >
                                  {cand.criteriDecisio || cand.motiu || 'Patró semàntic identificat'}
                                </Typography>
                              </Tooltip>

                              {/* Tokens Clau Avaluats */}
                              {cand.tokensAvaluats && Array.isArray(cand.tokensAvaluats) && cand.tokensAvaluats.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.4 }}>
                                  {cand.tokensAvaluats.map((tok, i) => (
                                    <Chip
                                      key={i}
                                      label={tok}
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: '0.62rem',
                                        backgroundColor: '#f3e8ff',
                                        color: '#6b21a8',
                                        fontWeight: 600,
                                        px: 0.2
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}
                            </TableCell>

                            {/* Botons d'acció individual (One-by-one execution) */}
                            <TableCell align="center">
                              {refineEstat === 'PENDENTS' ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  {/* Split Button: Acceptar individual + Dropdown per a iguals */}
                                  <ButtonGroup size="small" variant="contained" color="success">
                                    <Button
                                      onClick={() => handleAcceptItem(cand, false)}
                                      startIcon={<CheckIcon fontSize="small" />}
                                      title="Acceptar proposta només per a aquest moviment"
                                      sx={{ fontWeight: 'bold', fontSize: '0.72rem', px: 1, py: 0.3, textTransform: 'none' }}
                                    >
                                      Acceptar
                                    </Button>
                                    <Button
                                      size="small"
                                      color="success"
                                      onClick={(e) => {
                                        setSplitMenuAnchor(e.currentTarget);
                                        setSplitMenuCandidate(cand);
                                      }}
                                      title="Opcions: aplicar a tots els moviments idèntics"
                                      sx={{ px: 0.4 }}
                                    >
                                      <ArrowDropDownIcon fontSize="small" />
                                    </Button>
                                  </ButtonGroup>

                                  {/* Canviar concepte manualment */}
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    title="Modificar proposta jeràrquica"
                                    onClick={() => handleOpenTreeSelector(cand)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>

                                  {/* Descartar */}
                                  <IconButton
                                    size="small"
                                    color="default"
                                    title="Descartar aquesta proposta"
                                    onClick={() => handleDiscardItem(cand)}
                                  >
                                    <BlockIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  {/* Restaurar a pendents */}
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    startIcon={<RestoreIcon fontSize="small" />}
                                    onClick={() => handleRestoreItem(cand)}
                                    sx={{ fontWeight: 'bold', fontSize: '0.72rem', px: 1, py: 0.3, textTransform: 'none' }}
                                    title="Restaurar a la llista de pendents"
                                  >
                                    Restaurar
                                  </Button>

                                  {/* Acceptar proposta directa */}
                                  <IconButton
                                    size="small"
                                    color="success"
                                    title="Acceptar proposta ara"
                                    onClick={() => handleAcceptItem(cand, false)}
                                  >
                                    <CheckIcon fontSize="small" />
                                  </IconButton>

                                  {/* Canviar concepte manualment */}
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    title="Assignar un altre concepte manualment"
                                    onClick={() => handleOpenTreeSelector(cand)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>

          {/* 2. Vista Targetes a Mòbil (xs i sm, < 900px) */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
            {loadingCandidates ? (
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <CircularProgress size={36} />
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  {refineEstat === 'PENDENTS'
                    ? "Analitzant l'històric i cercant patrons d'afinament..."
                    : "Carregant moviments descartats..."}
                </Typography>
              </Paper>
            ) : candidates.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>
                  {refineEstat === 'PENDENTS' ? 'Tot afinat! Cap candidat pendent.' : 'No hi ha moviments descartats.'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {refineEstat === 'PENDENTS'
                    ? (lastSearchedParams
                      ? `No hi ha moviments pendents de refinar per a l'any ${lastSearchedParams.any} amb confiança >= ${Math.round(lastSearchedParams.minConfidence * 100)}%.`
                      : "No s'han trobat moviments per refinar amb els filtres indicats.")
                    : "Quan descartis propostes de refinament, apareixeran aquí per si vols reconsiderar-les."}
                </Typography>
              </Paper>
            ) : (
              candidates.map((cand) => {
                const isSelected = selectedIds.has(cand.concepteMovimentId);
                const colorImport = getColorForAmount(cand.importe, false);
                const confColor = getConfidenceColor(cand.confianca);
                const confPct = Math.round((cand.confianca || 0) * 100);
                const desc = cand.descripcio || cand.concepteDescripcio || cand.movimentDescripcio || cand.observacions;

                return (
                  <Paper
                    key={cand.concepteMovimentId}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderLeft: '4px solid',
                      borderLeftColor: cand.isCustomized ? 'success.main' : isSelected ? 'primary.main' : '#cfd8dc',
                      backgroundColor: cand.isCustomized ? '#f1f8e9' : isSelected ? '#f5f9ff' : '#ffffff',
                      boxShadow: isSelected ? '0 2px 8px rgba(25, 118, 210, 0.12)' : 'none',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {/* Capçalera Targeta: Checkbox + Data + Import */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggleRow(cand.concepteMovimentId)}
                          sx={{ p: 0.5 }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.85rem' }}>
                          {strDateToDDMMYYYY(cand.data)}
                        </Typography>
                        {cand.concepteMovimentId && (
                          <Link
                            href={`/${userapp}/cmovdetail/${cand.concepteMovimentId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title="Obrir fitxa del concepte-moviment en una nova pestanya"
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: 'text.secondary',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.3,
                              '&:hover': {
                                color: 'primary.main',
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            #{cand.concepteMovimentId}
                            <OpenInNewIcon sx={{ fontSize: '11px !important', opacity: 0.7 }} />
                          </Link>
                        )}
                      </Box>
                      <Typography variant="subtitle1" sx={{ color: colorImport, fontWeight: 'bold', fontSize: '1rem' }}>
                        {formatEuro(cand.importe)}
                      </Typography>
                    </Box>

                    {/* Nom bancari original */}
                    <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {cand.movimentNom || 'N/D'}
                    </Typography>

                    {/* Descripció del concepte / article si n'hi ha (visible directament a mòbil) */}
                    {desc && (
                      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocalOfferIcon sx={{ fontSize: 13, color: '#0288d1' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0277bd', fontSize: '0.82rem' }}>
                          {desc}
                        </Typography>
                      </Box>
                    )}

                    {/* Transició de Concepte (Actual -> Proposat) */}
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: '#fcfcfc', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                          TRANSICIÓ DE CONCEPTE:
                        </Typography>
                        <Chip
                          label={`${confPct}% confiança`}
                          size="small"
                          color={confColor}
                          sx={{ fontWeight: 'bold', height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                        {/* Node Actual */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ConcepteIcon code={cand.nodeActualCodi} concept={cand.nodeActualNom} level={cand.nodeActualNivell || 1} onlyIcon />
                          <Chip
                            label={cand.nodeActualNom || cand.pathActual || 'GENÈRIC'}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.75rem', borderColor: '#b0bec5' }}
                          />
                        </Box>

                        <EastIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />

                        {/* Node Proposat */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ConcepteIcon code={cand.nodeProposatCodi} concept={cand.nodeProposatNom} level={cand.nodeProposatNivell || 2} onlyIcon />
                          <Chip
                            label={cand.nodeProposatNom}
                            size="small"
                            color={cand.isCustomized ? "success" : "primary"}
                            sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                          />
                        </Box>
                      </Box>

                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, fontSize: '0.68rem' }}>
                        Ruta proposada: <strong>{cand.pathProposat || cand.nodeProposatNom}</strong>
                      </Typography>
                    </Paper>

                    {/* Criteri i Tokens Avaluats */}
                    {(cand.criteriDecisio || cand.motiu || (cand.tokensAvaluats && cand.tokensAvaluats.length > 0)) && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5, fontSize: '0.72rem' }}>
                          <strong>Criteri:</strong> {cand.criteriDecisio || cand.motiu || 'Patró semàntic identificat'}
                        </Typography>
                        {cand.tokensAvaluats && cand.tokensAvaluats.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                            {cand.tokensAvaluats.map((tok, i) => (
                              <Chip key={i} label={tok} size="small" sx={{ height: 18, fontSize: '0.65rem', backgroundColor: '#f3e8ff', color: '#6b21a8', fontWeight: 600 }} />
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Botons d'Acció de la targeta a Mòbil */}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 0.5 }}>
                      {refineEstat === 'PENDENTS' ? (
                        <>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              fullWidth
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckIcon />}
                              onClick={() => handleAcceptItem(cand, false)}
                              sx={{ fontWeight: 'bold', fontSize: '0.75rem', textTransform: 'none' }}
                            >
                              Acceptar
                            </Button>
                            <Button
                              fullWidth
                              size="small"
                              variant="outlined"
                              color="success"
                              onClick={() => handleAcceptItem(cand, true)}
                              sx={{ fontWeight: 'bold', fontSize: '0.72rem', textTransform: 'none' }}
                              title="Aplica a tots els moviments amb mateix nom, entitat i concepte"
                            >
                              Tots iguals
                            </Button>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<InfoOutlinedIcon />}
                              onClick={() => handleOpenContextDialog(cand)}
                              sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                            >
                              Context
                            </Button>

                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<EditIcon />}
                                onClick={() => handleOpenTreeSelector(cand)}
                                sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                              >
                                Modificar
                              </Button>
                              <IconButton
                                size="small"
                                color="default"
                                title="Descartar proposta"
                                onClick={() => handleDiscardItem(cand)}
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </>
                      ) : (
                        <>
                          <Button
                            fullWidth
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<RestoreIcon />}
                            onClick={() => handleRestoreItem(cand)}
                            sx={{ fontWeight: 'bold', fontSize: '0.8rem', textTransform: 'none' }}
                          >
                            Restaurar a Pendents
                          </Button>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<InfoOutlinedIcon />}
                              onClick={() => handleOpenContextDialog(cand)}
                              sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                            >
                              Context
                            </Button>

                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                startIcon={<CheckIcon />}
                                onClick={() => handleAcceptItem(cand, false)}
                                sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                              >
                                Acceptar
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<EditIcon />}
                                onClick={() => handleOpenTreeSelector(cand)}
                                sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                              >
                                Modificar
                              </Button>
                            </Box>
                          </Box>
                        </>
                      )}
                    </Box>
                  </Paper>
                );
              })
            )}
          </Box>
        </Stack>
      )}

      {/* ========================================================================= */}
      {/* PESTANYA 1: PROVADOR SEMÀNTIC (AMAZON & TEXT)                            */}
      {/* ========================================================================= */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          {/* Panell d'Entrada */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PsychologyIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Provador de Classificació de Text
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enganxa qualsevol títol d'article d'Amazon o text d'un concepte bancari per testejar en temps real la inferència del motor sobre l'arbre jeràrquic.
              </Typography>

              {/* Exemples ràpids clicables */}
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 1 }}>
                Exemples ràpids d'Amazon i despeses:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                {[
                  "Echo Dot 5a generació altaveu intel·ligent amb Alexa",
                  "Cafetera Nespresso De'Longhi Inissia",
                  "Pneumàtics Michelin Primacy 4",
                  "Xampú anticaspa H&S Classic 500ml",
                  "Llet sencera Pascual brik 1L",
                  "Subscripció anual Amazon Prime"
                ].map((ex, idx) => (
                  <Chip
                    key={idx}
                    label={ex}
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => {
                      setTesterText(ex);
                      handleRunTester(ex);
                    }}
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))}
              </Box>

              {/* Input de text */}
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Text o Títol de l'Article"
                placeholder="Exemple: 'Compra online Amazon ES: Cable USB-C càrrega ràpida 2m'"
                value={testerText}
                onChange={(e) => setTesterText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleRunTester();
                  }
                }}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setTesterText('');
                    setTesterResult(null);
                  }}
                  disabled={testerLoading || !testerText}
                >
                  Netejar
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={testerLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  disabled={testerLoading || !testerText.trim()}
                  onClick={() => handleRunTester()}
                  sx={{ fontWeight: 'bold', px: 3 }}
                >
                  {testerLoading ? 'Classificant...' : 'Analitzar Concepte'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Targeta de Resultats */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, borderRadius: 2, height: '100%', minHeight: '380px', backgroundColor: '#fafbfc' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Resultat de la Inferència
              </Typography>

              {testerLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '240px' }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                    Consultant el motor intel·ligent...
                  </Typography>
                </Box>
              ) : !testerResult ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '240px', color: 'text.secondary' }}>
                  <SearchIcon sx={{ fontSize: 50, mb: 1, opacity: 0.5 }} />
                  <Typography variant="body2">
                    Escriu un text o tria un dels exemples per veure el desglossament.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2.5}>
                  {/* Concepte Path & Confiança */}
                  <Card variant="outlined" sx={{ borderRadius: 2, backgroundColor: '#ffffff' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                          RUTA JERÀRQUICA DETECTADA:
                        </Typography>
                        <Chip
                          label={`Confiança: ${Math.round((testerResult.confidence || 0) * 100)}%`}
                          color={getConfidenceColor(testerResult.confidence || 0)}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Box>

                      {/* Breadcrumbs visuals dels segments */}
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        {(testerResult.conceptePath || '').split('/').map((seg, idx, arr) => (
                          <React.Fragment key={idx}>
                            <Chip
                              label={seg}
                              color={idx === arr.length - 1 ? "primary" : "default"}
                              variant={idx === arr.length - 1 ? "filled" : "outlined"}
                              sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}
                            />
                            {idx < arr.length - 1 && <EastIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                          </React.Fragment>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Concepte Clàssic Derivat */}
                  <Card variant="outlined" sx={{ borderRadius: 2, backgroundColor: '#ffffff' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 1 }}>
                        CONCEPTE CLÀSSIC EQUIVALENT (BBDD):
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ConcepteIcon
                          code={testerResult.codi || testerResult.concepte || testerResult.concepteClassic}
                          concept={testerResult.concepte || testerResult.concepteClassic}
                        />
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {testerResult.concepte || testerResult.concepteClassic || 'N/D'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Tokens identificats */}
                  {testerResult.tokens && testerResult.tokens.length > 0 && (
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                        TOKENS CLAU IDENTIFICATS:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                        {testerResult.tokens.map((tok, idx) => (
                          <Chip key={idx} label={tok} size="small" sx={{ backgroundColor: '#e0f2fe', color: '#0369a1' }} />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Detalls tècnics dels Nodes resolts */}
                  <Divider />
                  <Box sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                      METADADES DELS NODES ASSIGNATS:
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid size={6}>
                        <strong>Node N1:</strong> {testerResult.nodeN1?.nom || (testerResult.nodeN1 ? `#${testerResult.nodeN1}` : 'N/D')}
                      </Grid>
                      <Grid size={6}>
                        <strong>Node N2:</strong> {testerResult.nodeN2?.nom || (testerResult.nodeN2 ? `#${testerResult.nodeN2}` : 'N/D')}
                      </Grid>
                      <Grid size={6}>
                        <strong>Node N3:</strong> {testerResult.nodeN3?.nom || (testerResult.nodeN3 ? `#${testerResult.nodeN3}` : 'N/D')}
                      </Grid>
                      <Grid size={6}>
                        <strong>Node Fulla:</strong> {testerResult.nodeFulla?.nom || (testerResult.nodeFulla ? `#${testerResult.nodeFulla}` : 'N/D')}
                      </Grid>
                    </Grid>
                  </Box>
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ========================================================================= */}
      {/* PESTANYA 2: AUDITORIA D'ALINEACIÓ                                        */}
      {/* ========================================================================= */}
      {activeTab === 2 && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Diagnòstic d'Alineació Jeràrquica
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Verifica la consistència entre els conceptes clàssics i els nodes jeràrquics a la base de dades.
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="primary"
              startIcon={alignmentLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              onClick={handleFetchAlignment}
              disabled={alignmentLoading}
            >
              {alignmentLoading ? 'Comprovant...' : 'Carregar Diagnòstic'}
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          {alignmentLoading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress size={36} />
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                Consultant l'estat d'alineació dels moviments...
              </Typography>
            </Box>
          ) : !alignmentData ? (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
              <AssessmentIcon sx={{ fontSize: 50, mb: 1, opacity: 0.5 }} />
              <Typography variant="body1">
                Fes clic a «Carregar Diagnòstic» per comprovar l'estat de tots els moviments.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                S'han analitzat <strong>{alignmentData.length || 0}</strong> registres a l'auditoria.
              </Alert>

              <TableContainer sx={{ maxHeight: 500 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f4f6f8' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Concepte Clàssic</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Concepte Jeràrquic (Path)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Estat d'Alineació</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {alignmentData.slice(0, 50).map((row, idx) => (
                      <TableRow key={row.id || idx} hover>
                        <TableCell>#{row.id || row.concepteMovimentId || idx}</TableCell>
                        <TableCell>{strDateToDDMMYYYY(row.data)}</TableCell>
                        <TableCell>{row.concepte || row.concepteClassic || 'N/D'}</TableCell>
                        <TableCell>{row.conceptePath || row.path || 'Sense assignar'}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.alineat ? 'Alineat' : (row.conceptePath ? 'Desalineat' : 'Sense Jerarquia')}
                            size="small"
                            color={row.alineat ? 'success' : (row.conceptePath ? 'warning' : 'default')}
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {alignmentData.length > 50 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right' }}>
                  Mostrant els primers 50 resultats de {alignmentData.length}.
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* ========================================================================= */}
      {/* MODALS I DIÀLEGS                                                          */}
      {/* ========================================================================= */}

      {/* Modal Confirmació Aplicar Seleccionats */}
      <Dialog open={confirmSelectedOpen} onClose={() => !isExecuting && setConfirmSelectedOpen(false)}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Confirmar Aplicació del Refinament
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Estàs a punt d'aplicar el refinament per als <strong>{selectedIds.size}</strong> moviments seleccionats.
            Aquesta acció actualitzarà oficialment les branques jeràrquiques i els conceptes corresponents al backend.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmSelectedOpen(false)} disabled={isExecuting}>
            Cancel·lar
          </Button>
          <Button
            onClick={handleExecuteSelected}
            variant="contained"
            color="success"
            disabled={isExecuting}
            startIcon={isExecuting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
          >
            {isExecuting ? 'Aplicant...' : 'Confirmar i Aplicar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Confirmació Aprovar Tots Automàticament */}
      <Dialog open={confirmAllOpen} onClose={() => !isExecuting && setConfirmAllOpen(false)}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Aprovar Tots els Candidats Automàticament
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Vols aprovar automàticament totes les propostes pendents per a l'any <strong>{selectedAny === 'ALL' ? 'Tots els anys' : selectedAny}</strong>?
            El backend processarà totes les propostes que compleixin el criteri de confiança.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmAllOpen(false)} disabled={isExecuting}>
            Cancel·lar
          </Button>
          <Button
            onClick={handleExecuteAll}
            variant="contained"
            color="secondary"
            disabled={isExecuting}
            startIcon={isExecuting ? <CircularProgress size={18} color="inherit" /> : <DoneAllIcon />}
          >
            {isExecuting ? 'Aprovant...' : 'Aprovar Tots'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Menú contextual per a l'Split Button d'Acceptar (opció Tots els moviments idèntics) */}
      <Menu
        anchorEl={splitMenuAnchor}
        open={Boolean(splitMenuAnchor)}
        onClose={() => {
          setSplitMenuAnchor(null);
          setSplitMenuCandidate(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            const cand = splitMenuCandidate;
            setSplitMenuAnchor(null);
            setSplitMenuCandidate(null);
            handleAcceptItem(cand, true);
          }}
          sx={{ py: 1, px: 2 }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main', display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <CheckIcon fontSize="small" /> Acceptar per a tots els moviments idèntics
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Aplica aquesta proposta a tots els moviments amb el mateix nom, entitat i concepte actual
            </Typography>
          </Box>
        </MenuItem>
      </Menu>

      {/* Modal Rectificar Proposta Jeràrquica amb SelectConcepteTree Canònic */}
      <Dialog
        open={treeDialogOpen}
        onClose={() => {
          setTreeDialogOpen(false);
          setEditingCandidate(null);
          setSelectedTreeOption(null);
          setManualAplicarIguals(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#f8fafc' }}>
          <EditIcon color="primary" />
          Assignar Concepte Jeràrquic Manualment
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {editingCandidate && (
            <Stack spacing={2.5}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: '#fcfcfc' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {editingCandidate.movimentNom || 'N/D'}
                </Typography>
                {editingCandidate.descripcio && (
                  <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, mt: 0.25 }}>
                    🏷️ {editingCandidate.descripcio}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: '1px solid #f0f0f0', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Data: <strong>{strDateToDDMMYYYY(editingCandidate.data)}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Import: <strong>{formatEuro(editingCandidate.importe)}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Actual: <strong>{editingCandidate.nodeActualNom || editingCandidate.pathActual}</strong>
                  </Typography>
                </Box>
              </Paper>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Tria el nou concepte jeràrquic canònic:
                </Typography>
                <SelectConcepteTree
                  label="Concepte Jeràrquic"
                  value={selectedTreeOption ? `node:${selectedTreeOption.id}` : (editingCandidate?.nodeProposatId ? `node:${editingCandidate.nodeProposatId}` : (editingCandidate?.pathProposat || editingCandidate?.nodeProposatNom))}
                  onChange={(event, selectedNode) => {
                    const node = selectedNode || event?.target?.node;
                    if (node) {
                      setSelectedTreeOption(node);
                    }
                  }}
                  tree={conceptesTree}
                  entitats={entitatsTransversals}
                  allowRootSelection={true}
                  onlyLeaves={false}
                  fullWidth
                />
              </Box>

              {/* Opció per aplicar a tots els moviments idèntics */}
              <Box sx={{ pt: 1, borderTop: '1px solid #f0f0f0' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={manualAplicarIguals}
                      onChange={(e) => setManualAplicarIguals(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.88rem' }}>
                      Aplicar també a <strong>tots els moviments idèntics</strong> (mateix nom, entitat i concepte)
                    </Typography>
                  }
                />
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={() => {
              setTreeDialogOpen(false);
              setEditingCandidate(null);
              setSelectedTreeOption(null);
              setManualAplicarIguals(false);
            }}
            variant="outlined"
          >
            Cancel·lar
          </Button>
          <Button
            onClick={handleApplyManualSelection}
            variant="contained"
            color="primary"
            disabled={!selectedTreeOption}
            sx={{ fontWeight: 'bold' }}
          >
            Confirmar i Aplicar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Detall de Context Bancari */}
      <Dialog
        open={contextDialogOpen}
        onClose={() => setContextDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#f8fafc' }}>
          <AccountBalanceIcon color="primary" />
          Context Bancari del Moviment
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {contextCandidate && (
            <Stack spacing={2}>
              {/* Nom i Import */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: '#fcfcfc' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {contextCandidate.movimentNom || 'N/D'}
                </Typography>
                {contextCandidate.descripcio && (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      icon={<LocalOfferIcon sx={{ fontSize: '13px !important' }} />}
                      label={contextCandidate.descripcio}
                      size="small"
                      sx={{ backgroundColor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}
                    />
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                  <Typography variant="body2" color="text.secondary">
                    Import: <strong style={{ color: getColorForAmount(contextCandidate.importe, false), fontSize: '1rem' }}>{formatEuro(contextCandidate.importe)}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Data: <strong>{strDateToDDMMYYYY(contextCandidate.data)}</strong>
                  </Typography>
                </Box>
              </Paper>

              {/* Dades del Compte i Operació */}
              <Grid container spacing={1.5}>
                <Grid size={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Entitat Origen</Typography>
                  <Typography variant="body2" fontWeight={600}>{contextCandidate.entitatOrigen || 'N/D'}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Contracte / Compte</Typography>
                  <Typography variant="body2" fontWeight={600}>{contextCandidate.contracte || 'N/D'}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Data Valor</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {contextCandidate.dataValor ? strDateToDDMMYYYY(contextCandidate.dataValor) : 'N/D'}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Saldo Resultant</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {contextCandidate.saldo != null ? formatEuro(contextCandidate.saldo) : 'N/D'}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary" display="block">Concepte Clàssic Original (BBDD)</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.3 }}>
                    <ConcepteIcon code={contextCandidate.concepteClassic} concept={contextCandidate.concepteClassic} onlyIcon />
                    <Typography variant="body2" fontWeight={600}>{contextCandidate.concepteClassic || 'N/D'}</Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Info addicional bancària si n'hi ha */}
              {(contextCandidate.mesInfo || contextCandidate.mesInfoDetall) && (
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, backgroundColor: '#f8fafc', borderColor: '#e2e8f0' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    INFORMACIÓ ADDICIONAL DEL BANC:
                  </Typography>
                  {contextCandidate.mesInfo && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Info:</strong> {contextCandidate.mesInfo}
                    </Typography>
                  )}
                  {contextCandidate.mesInfoDetall && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                      <strong>Detall:</strong> {contextCandidate.mesInfoDetall}
                    </Typography>
                  )}
                </Paper>
              )}

              {/* Transparència de decisió */}
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, backgroundColor: '#faf5ff', borderColor: '#e9d5ff' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#6b21a8', display: 'block', mb: 0.5 }}>
                  CRITERI DE REFINAMENT I TOKENS DETECTATS:
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, color: '#3b0764' }}>
                  {contextCandidate.criteriDecisio || contextCandidate.motiu || 'Afinament proposat per algoritme'}
                </Typography>
                {contextCandidate.tokensAvaluats && Array.isArray(contextCandidate.tokensAvaluats) && contextCandidate.tokensAvaluats.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {contextCandidate.tokensAvaluats.map((tok, i) => (
                      <Chip key={i} label={tok} size="small" sx={{ backgroundColor: '#e9d5ff', color: '#581c87', fontWeight: 600, fontSize: '0.7rem' }} />
                    ))}
                  </Box>
                )}
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          {contextCandidate?.concepteMovimentId && (
            <Button
              href={`/${userapp}/cmovdetail/${contextCandidate.concepteMovimentId}`}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Obrir Fitxa Detall #{contextCandidate.concepteMovimentId}
            </Button>
          )}
          <Button onClick={() => setContextDialogOpen(false)} variant="contained" color="inherit">
            Tancar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notificacions */}
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%', fontWeight: 500 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default RefinamentJerarquicPage;
